import { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Download, Save, Sparkles, Loader2, Wand2, ArrowLeft, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg, FILES_BASE } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ProjectSelect, useProjects } from "@/components/common";
import { FORMATS, DEFAULT_LAYOUT, AssetPicker, ScenePicker, drawGreeting, recommendLogo } from "@/components/GreetingParts";

const LOGO_POS = [["auto", "Auto"], ["top-center", "Top centre"], ["top-left", "Top left"], ["top-right", "Top right"], ["bottom-left", "Bottom left"], ["bottom-right", "Bottom right"], ["custom", "Custom"]];
const FOOT = [["project", "Project name"], ["company_name", "Company name"], ["phone", "Phone"], ["email", "Email"], ["website", "Website"], ["address", "Address"], ["rera", "RERA"], ["instagram", "Instagram"], ["cta", "Call to action"], ["disclaimer", "Disclaimer"]];

function LogoControls({ s, set, setLayout, b, w, reload }) {
  const { me } = useAuth();
  const saveAlt = async () => { try { await api.put("/org/branding", { alt_logo_url: s.logoUpload }); await reload(); set({ logoSrc: "alternate", logoAsked: true }); toast.success("Saved as your alternate approved logo"); } catch (e) { toast.error(errMsg(e)); } };
  const opts = [["brand", "Approved brand logo"], ...(b.alt_logo_url ? [["alternate", "Alternate approved logo"]] : []), ["upload", "Upload a different logo"]];
  return (
    <div className="space-y-3" data-testid="logo-controls">
      <label className="flex items-center gap-2 text-sm"><Switch checked={s.logoOn} onCheckedChange={(v) => set({ logoOn: v })} data-testid="greeting-logo-toggle" />Show logo (one logo per creative)</label>
      {s.logoOn && <>
        <Seg value={s.logoSrc} onChange={(v) => set({ logoSrc: v })} testid="greeting-logo-src" options={opts} />
        {s.logoSrc === "upload" && <AssetPicker kind="logo" projectId={s.projectId} value={s.logoUpload} onChange={(v) => set({ logoUpload: v, logoAsked: false })} format={s.format} canWrite={w} />}
        {s.logoSrc === "upload" && s.logoUpload && !s.logoAsked && (
          <div className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-3 text-xs space-y-2" data-testid="logo-replace-question">
            <div>This logo will <b>replace</b> your approved brand logo on this creative (never both). How should we use it?</div>
            <div className="flex flex-wrap gap-2"><Button size="sm" variant="secondary" onClick={() => set({ logoAsked: true })} data-testid="logo-use-once">Only for this creative</Button>
              {["developer_admin", "marketing_agent"].includes(me?.user?.role) && <Button size="sm" onClick={saveAlt} data-testid="logo-save-alternate">Save as alternate approved logo</Button>}</div>
          </div>
        )}
        <div className="space-y-1"><Label className="text-xs">Logo position · Recommended: {LOGO_POS.find((x) => x[0] === recommendLogo(s))[1]}</Label>
          <Seg value={s.layout.logoPos} onChange={(v) => setLayout({ logoPos: v })} testid="logo-pos" options={LOGO_POS} /></div>
        <div className="grid sm:grid-cols-2 gap-3">
          <Range label="Logo size" k="logoScale" min={0.5} max={2} step={0.1} s={s} setLayout={setLayout} />
          <Range label="Logo margin (safe area %)" k="logoMargin" min={0} max={12} step={1} s={s} setLayout={setLayout} />
          {s.layout.logoPos === "custom" && <><Range label="Horizontal position %" k="logoX" min={5} max={95} step={1} s={s} setLayout={setLayout} /><Range label="Vertical position %" k="logoY" min={2} max={90} step={1} s={s} setLayout={setLayout} /></>}
        </div>
      </>}
    </div>
  );
}

function FooterToggles({ s, set, pub }) {
  const avail = FOOT.filter(([k]) => (k === "project" ? !!s.projectId : !!pub[k]));
  return (
    <div className="space-y-2" data-testid="greeting-footer-toggles">
      <Label className="text-xs">Footer on this creative</Label>
      <div className="flex flex-wrap gap-3">{avail.map(([k, l]) => (
        <label key={k} className="flex items-center gap-1 text-xs"><Switch checked={s.fshow[k] !== false} onCheckedChange={(v) => set({ fshow: { ...s.fshow, [k]: v } })} data-testid={`greeting-footer-${k}`} />{l}</label>))}</div>
      <p className="text-[11px] text-slate-500">{pub.company_name ? "" : "No public company name set, so none is shown. "}Add contact details in <Link to="/branding" className="underline">Organization → Branding → Footer & Contact</Link>.</p>
    </div>
  );
}

const STYLES = ["Classic", "Light", "Bold"];

function Pick({ label, value, onChange, options, testid }) {
  return (
    <div className="space-y-1"><Label className="text-xs">{label}</Label>
      <Select value={value} onValueChange={onChange}><SelectTrigger data-testid={testid}><SelectValue /></SelectTrigger>
        <SelectContent>{options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent></Select></div>
  );
}

function Seg({ value, onChange, options, testid }) {
  return (
    <div className="flex flex-wrap gap-2">{options.map(([v, l]) => (
      <Button key={v} size="sm" variant={value === v ? "default" : "secondary"} onClick={() => onChange(v)} data-testid={`${testid}-${v}`}>{l}</Button>))}</div>
  );
}

function Range({ label, k, min, max, step, s, setLayout }) {
  return (
    <label className="text-xs space-y-1 block"><span className="flex justify-between"><span>{label}</span><span className="font-mono">{s.layout[k]}</span></span>
      <input type="range" min={min} max={max} step={step} value={s.layout[k]} onChange={(e) => setLayout({ [k]: Number(e.target.value) })} className="w-full accent-emerald-600" data-testid={`layout-${k}`} /></label>
  );
}

function Basics({ s, set, meta, setFest }) {
  return (
    <div className="grid sm:grid-cols-2 gap-4">
      <Pick label="Festival" value={s.festival} onChange={setFest} options={meta.festivals.map((f) => f.name)} testid="greeting-festival-select" />
      <div className="space-y-1"><Label className="text-xs">Project</Label><ProjectSelect value={s.projectId} onChange={(v) => set({ projectId: v })} className="w-full" testid="greeting-project-select" /></div>
      <div className="sm:col-span-2 space-y-1"><Label className="text-xs">Format (size)</Label>
        <Seg value={s.format} onChange={(v) => set({ format: v })} options={Object.entries(FORMATS).map(([k, [w, h]]) => [k, `${k} · ${w}×${h}`])} testid="greeting-format" /></div>
    </div>
  );
}

function Artwork({ s, set, w, busy, aiBg, reload }) {
  return (
    <div className="space-y-4">
      <Seg value={s.art} onChange={(v) => set({ art: v })} testid="greeting-mode" options={[["template", "Brand design (free)"], ["upload", "Upload my template"], ["ai", "AI picture with people"]]} />
      {s.art === "template" && <Pick label="Design style" value={s.style} onChange={(v) => set({ style: v })} options={STYLES} testid="greeting-style-select" />}
      {s.art === "upload" && <AssetPicker kind="template" projectId={s.projectId} value={s.template} onChange={(v) => set({ template: v })} format={s.format} canWrite={w} />}
      {s.art === "ai" && (
        <div className="space-y-3">
          <Label className="text-xs">Who should be in the picture? Pick a suggestion or describe it (man, woman, kids, family, elders, a girl doing yoga...)</Label>
          <ScenePicker festival={s.festival} projectId={s.projectId} value={s.scene} onChange={(v) => set({ scene: v })} canWrite={w} onCredits={reload} />
          {w && <Button onClick={aiBg} disabled={!!busy} data-testid="greeting-ai-bg-button">{busy === "bg" ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Sparkles className="h-4 w-4 mr-1" />}{s.bg ? "Create again" : "Create AI picture"} (uses image credits)</Button>}
          {busy === "bg" && <p className="text-xs text-slate-500">Creating your picture. This takes up to a minute.</p>}
        </div>
      )}
    </div>
  );
}

function Photos({ s, set, w, setLayout, b, reload }) {
  return (
    <div className="space-y-4">
      <div className="space-y-1"><Label className="text-xs">Logo</Label>
        <LogoControls s={s} set={set} setLayout={setLayout} b={b} w={w} reload={reload} /></div>
      <div className="space-y-1"><Label className="text-xs">Building / project photo (optional)</Label>
        <AssetPicker kind="building" projectId={s.projectId} value={s.building} onChange={(v) => set({ building: v })} format={s.format} canWrite={w} /></div>
      <div className="space-y-2">
        <label className="flex items-center gap-2 text-sm"><Switch checked={s.useAmbassador} onCheckedChange={(v) => set({ useAmbassador: v })} data-testid="greeting-ambassador-toggle" />Add brand ambassador photo</label>
        {s.useAmbassador && <AssetPicker kind="ambassador" projectId={s.projectId} value={s.ambassador} onChange={(v) => set({ ambassador: v })} format={s.format} canWrite={w} />}</div>
    </div>
  );
}

function Message({ s, set, meta, w, busy, aiMsg, pub }) {
  return (
    <div className="space-y-4">
      <div className="space-y-1"><Label className="text-xs">Heading</Label><Input value={s.title} onChange={(e) => set({ title: e.target.value })} data-testid="greeting-title-input" /></div>
      <div className="grid sm:grid-cols-2 gap-3">
        <Pick label="Language" value={s.language} onChange={(v) => set({ language: v })} options={meta.languages} testid="greeting-language-select" />
        <div className="space-y-1"><Label className="text-xs">How to write the message</Label>
          <Seg value={s.msgMode} onChange={(v) => set({ msgMode: v })} testid="greeting-msg-mode" options={[["manual", "I'll type it"], ["ai", "AI writes it"]]} /></div>
      </div>
      {s.msgMode === "ai" && w && <Button size="sm" variant="secondary" onClick={aiMsg} disabled={!!busy} data-testid="greeting-ai-message-button">{busy === "msg" ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Wand2 className="h-4 w-4 mr-1" />}Write with AI in {s.language} (small text credit)</Button>}
      <Textarea rows={3} value={s.message} onChange={(e) => set({ message: e.target.value })} data-testid="greeting-message-input" />
      <FooterToggles s={s} set={set} pub={pub} />
      <p className="text-[11px] text-slate-500">Add prices, offers or claims only if they are approved.</p>
    </div>
  );
}

function Layout({ s, set, setLayout }) {
  return (
    <div className="grid sm:grid-cols-2 gap-4" data-testid="greeting-layout-controls">
      <Range label="Heading size" k="titleScale" min={0.5} max={1.6} step={0.1} s={s} setLayout={setLayout} />
      <Range label="Message size" k="msgScale" min={0.6} max={1.6} step={0.1} s={s} setLayout={setLayout} />
      <Range label="Background darkness" k="overlay" min={0} max={0.8} step={0.05} s={s} setLayout={setLayout} />
      <Range label="Photo size" k="photoSize" min={0.15} max={0.45} step={0.01} s={s} setLayout={setLayout} />
      <div className="space-y-1"><Label className="text-xs">Photo position</Label><Seg value={s.layout.photoPos} onChange={(v) => setLayout({ photoPos: v })} testid="layout-photoPos" options={[["bottom", "Bottom centre"], ["corners", "Bottom corners"]]} /></div>
      <Range label="Footer size" k="footerScale" min={0.6} max={1.5} step={0.1} s={s} setLayout={setLayout} />
      <div className="space-y-1"><Label className="text-xs">Footer alignment</Label><Seg value={s.layout.footerAlign} onChange={(v) => setLayout({ footerAlign: v })} testid="layout-footerAlign" options={[["center", "Centre"], ["left", "Left"]]} /></div>
      <Pick label="Design style (brand design)" value={s.style} onChange={(v) => set({ style: v })} options={STYLES} testid="greeting-style-expert" />
      <Button variant="ghost" size="sm" onClick={() => setLayout(DEFAULT_LAYOUT)} data-testid="layout-reset">Reset layout</Button>
    </div>
  );
}

const SIMPLE = [["Festival & size", Basics], ["Picture", Artwork], ["Logo & photos", Photos], ["Message", Message]];

export default function Greetings({ onSaved }) {
  const { can, reload } = useAuth();
  const { me } = useAuth();
  const projects = useProjects();
  const b = me?.organization?.branding || {};
  const [meta, setMeta] = useState({ festivals: [], languages: ["English"] });
  const [params] = useSearchParams();
  const initFest = params.get("festival") || "Diwali";
  const [pub, setPub] = useState({});
  const [s, setS] = useState({ festival: initFest, language: "English", format: "Instagram Post", style: "Classic", art: "template", title: params.get("title") || (initFest === "Custom" ? "Season's Greetings" : initFest === "Eid" ? "Eid Mubarak" : `Happy ${initFest}`), message: "",
    projectId: "", bg: null, template: null, scene: "", logoOn: true, logoSrc: "brand", logoUpload: null, logoAsked: false, building: null, useAmbassador: false, ambassador: null, msgMode: "manual", fshow: {}, layout: DEFAULT_LAYOUT });
  const [ui, setUi] = useState({ mode: "simple", step: 0 });
  const [busy, setBusy] = useState("");
  const ref = useRef(null);
  const set = (p) => setS((x) => ({ ...x, ...p }));
  const setLayout = (p) => setS((x) => ({ ...x, layout: { ...x.layout, ...p } }));
  const project = projects.find((p) => p.id === s.projectId);
  const brandLogo = b.logo_url || "/logo.webp";
  const on = (k) => s.fshow[k] !== false;
  const pick = (...ks) => ks.filter((k) => pub[k] && on(k)).map((k) => (k === "rera" ? `RERA: ${pub[k]}` : k === "instagram" ? `Instagram: ${pub[k]}` : pub[k]));
  const footerLines = [[project && on("project") ? project.name : null, ...pick("company_name")].filter(Boolean).join(" · "), pick("phone", "email", "website").join(" · "),
    pick("address").join(""), pick("rera", "instagram").join(" · "), pick("disclaimer").join("")].filter(Boolean);
  const spec = { ...s, primary: b.primary_color || "#0B5D36", secondary: b.secondary_color || "#C9A227", footerLines, cta: on("cta") ? pub.cta : null,
    logo: !s.logoOn ? null : s.logoSrc === "upload" ? s.logoUpload : s.logoSrc === "alternate" ? b.alt_logo_url : brandLogo, ambassador: s.useAmbassador ? s.ambassador : null };
  useEffect(() => { api.get("/footer", { params: { project_id: s.projectId || undefined } }).then((r) => setPub(r.data.public)); }, [s.projectId]);

  useEffect(() => { api.get("/greetings/festivals").then((r) => { setMeta(r.data); set({ message: (r.data.festivals.find((x) => x.name === initFest) || r.data.festivals[0])?.default_message || "" }); }); }, []); // eslint-disable-line
  useEffect(() => { if (ref.current) drawGreeting(ref.current, spec); }); // eslint-disable-line
  const setFest = (f) => { const d = meta.festivals.find((x) => x.name === f); set({ festival: f, title: f === "Custom" ? "Season's Greetings" : f === "Eid" ? "Eid Mubarak" : `Happy ${f}`, message: d?.default_message || s.message, bg: null, scene: "" }); };

  const aiBg = async () => {
    setBusy("bg");
    try {
      const { data } = await api.post("/greetings/background", { festival: s.festival, style: s.style, project_id: s.projectId || null, scene: s.scene || null });
      reload();
      for (let i = 0; i < 30; i++) {
        await new Promise((r) => setTimeout(r, 4000));
        const d = (await api.get(`/greetings/background/${data.id}`)).data;
        if (d.status === "completed") { set({ bg: FILES_BASE + d.file_url }); toast.success("AI picture ready"); return; }
        if (d.status === "failed") { toast.error("Picture failed; credits refunded"); reload(); return; }
      }
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };
  const aiMsg = async () => {
    setBusy("msg");
    try { const { data } = await api.post("/greetings/message", { festival: s.festival, language: s.language, project_id: s.projectId || null }); set({ message: data.message }); reload(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };
  const blob = () => new Promise((res) => ref.current.toBlob(res, "image/png"));
  const download = async () => { const a = document.createElement("a"); a.href = URL.createObjectURL(await blob()); a.download = `${s.festival}-${s.format}.png`.replace(/\s/g, "-"); a.click(); };
  const save = async () => {
    const fd = new FormData(); fd.append("file", await blob(), "greeting.png"); fd.append("festival", s.festival); fd.append("project_id", s.projectId); fd.append("fmt", s.format);
    try { await api.post("/greetings", fd); toast.success("Saved to library. You can now pick it in Campaign Center."); onSaved?.(); } catch (e) { toast.error(errMsg(e)); }
  };
  const w = can("marketing", "write");
  const props = { s, set, setLayout, meta, setFest, w, busy, aiBg, aiMsg, reload, pub, b };
  const [stepLabel, StepComp] = SIMPLE[ui.step];

  return (
    <div className="mb-8 space-y-4" data-testid="greetings-studio">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Seg value={ui.mode} onChange={(v) => setUi({ ...ui, mode: v })} testid="greeting-ui-mode" options={[["simple", "Simple (step by step)"], ["expert", "Expert (all controls)"]]} />
        <span className="text-[11px] text-slate-500">Uses your saved logo and brand colours from Organization → Branding.</span>
      </div>
      <div className="grid lg:grid-cols-[1fr_minmax(0,420px)] gap-6">
        <div className="lx-card min-w-0">
          {ui.mode === "simple" ? (
            <div data-testid="greeting-simple">
              <div className="flex gap-2 mb-4 overflow-x-auto">{SIMPLE.map(([l], i) => (
                <button key={l} onClick={() => setUi({ ...ui, step: i })} className={`shrink-0 text-xs px-3 py-1 rounded-full ${i === ui.step ? "bg-emerald-500/10 text-emerald-500 font-semibold" : "text-slate-400"}`} data-testid={`greeting-step-${i}`}>{i + 1}. {l}</button>))}</div>
              <div className="font-semibold mb-3">Step {ui.step + 1} of 4 · {stepLabel}</div>
              <StepComp {...props} />
              <div className="flex justify-between mt-6">
                <Button variant="ghost" disabled={!ui.step} onClick={() => setUi({ ...ui, step: ui.step - 1 })} data-testid="greeting-prev"><ArrowLeft className="h-4 w-4 mr-1" />Back</Button>
                {ui.step < 3 ? <Button onClick={() => setUi({ ...ui, step: ui.step + 1 })} data-testid="greeting-next">Next<ArrowRight className="h-4 w-4 ml-1" /></Button>
                  : <span className="text-xs text-emerald-500 self-center">Done. Download or save your greeting.</span>}
              </div>
            </div>
          ) : (
            <div className="space-y-6" data-testid="greeting-expert">
              {[...SIMPLE, ["Layout & sizes", Layout]].map(([l, C]) => <section key={l}><div className="font-semibold mb-3">{l}</div><C {...props} /></section>)}
            </div>
          )}
        </div>
        <div className="space-y-3 lg:sticky lg:top-4 self-start">
          <canvas ref={ref} className="w-full h-auto rounded-xl border border-slate-800 shadow-sm" data-testid="greeting-canvas" />
          <div className="flex gap-2">
            <Button className="flex-1" variant="secondary" onClick={download} data-testid="greeting-download-button"><Download className="h-4 w-4 mr-1" />Download PNG</Button>
            {w && <Button className="flex-1" onClick={save} data-testid="greeting-save-button"><Save className="h-4 w-4 mr-1" />Save to library</Button>}
          </div>
          <p className="text-[11px] text-slate-500 text-center">{s.format}: {FORMATS[s.format][0]} × {FORMATS[s.format][1]} px</p>
        </div>
      </div>
    </div>
  );
}
